import { BatchRecordWrite, DEFAULT_BATCH_CONCURRENCY, MAX_BATCH_SIZE } from './batching';

/**
 * Field agnostic change planning for bulk edits.
 *
 * A plan is computed in full before anything is written, so the preview the user confirms is
 * exactly what runs, and the captured previous values are what an undo replays.
 */

export type BulkOperationId =
	| 'set'
	| 'increase'
	| 'decrease'
	| 'shiftDays'
	| 'shiftHours'
	| 'approve'
	| 'reject'
	| 'softDelete'
	| 'restore'
	| 'permanentDelete';

export type BulkInputType = 'text' | 'number' | 'date' | 'datetime';

export interface BulkFieldOperation {
	id: BulkOperationId;
	label: string;
	/** Operations such as Approve or Restore need no value from the user. */
	inputType?: BulkInputType;
	inputLabel?: string;
	destructive?: boolean;
}

export type BulkComputeStatus = 'change' | 'unchanged' | 'blocked';

export interface BulkComputeResult {
	status: BulkComputeStatus;
	newValue?: any;
	/** Set for a permanent deletion, which removes the record rather than writing to it. */
	remove?: boolean;
	reason?: string;
}

export interface BulkFieldDefinition {
	/** Distinguishes entries that target the same field, such as delete versus permanent delete. */
	key?: string;
	field: string;
	label: string;
	operations: BulkFieldOperation[];
	/** Renders a value for the preview table. */
	formatValue: (value: any) => string;
	/** Rejects bad input before any record is touched. Returns an error message or null. */
	validateInput?: (operation: BulkOperationId, input: string) => string | null;
	compute: (record: any, operation: BulkOperationId, input: string) => BulkComputeResult;
}

/** One field's before and after, so a row can describe several changes at once. */
export interface BulkFieldChange {
	field: string;
	label: string;
	currentValue: any;
	newValue: any;
	currentLabel: string;
	newLabel: string;
}

/** Why a row is in the plan. Used by imports, which mix several kinds of row. */
export type BulkRowGroup = 'matched' | 'missingFromFile' | 'notFound';

export interface BulkChangeRow {
	id: string;
	record: any;
	status: BulkComputeStatus;
	reason?: string;
	currentValue: any;
	newValue?: any;
	currentLabel: string;
	newLabel: string;
	remove?: boolean;
	/** Set when a row changes more than one field, as an imported row does. */
	changes?: BulkFieldChange[];
	group?: BulkRowGroup;
}

export interface BulkChangePlan {
	field: string;
	fieldLabel: string;
	operation: BulkOperationId;
	operationLabel: string;
	input: string;
	destructive: boolean;
	rows: BulkChangeRow[];
	changeCount: number;
	unchangedCount: number;
	blockedCount: number;
}

/**
 * Estimates how long a run will take.
 *
 * Time is dominated by batch round trips, not by the number of records: 200 records and
 * 1500 records both commit in a single round. The per commit figure is deliberately
 * pessimistic, and the live ETA replaces this with measured throughput once the run starts.
 */
const COMMIT_LATENCY_MS = 1500;

export function estimateDurationMs(recordCount: number) {
	if (recordCount <= 0) {
		return 0;
	}

	let batches = Math.ceil(recordCount / MAX_BATCH_SIZE);
	let rounds = Math.ceil(batches / DEFAULT_BATCH_CONCURRENCY);

	return rounds * COMMIT_LATENCY_MS;
}

/**
 * Wording for the estimate. Network latency dominates and cannot be known in advance, so
 * short runs avoid a falsely precise number.
 */
export function describeDuration(recordCount: number) {
	let estimate = estimateDurationMs(recordCount);

	if (estimate <= COMMIT_LATENCY_MS * 2) {
		return 'Usually takes a few seconds.';
	}

	return `Usually takes around ${formatDuration(estimate)}.`;
}

export function formatDuration(ms?: number | null) {
	if (ms === null || ms === undefined || !isFinite(ms) || ms < 0) {
		return '—';
	}

	let seconds = Math.round(ms / 1000);

	if (seconds < 1) {
		return 'less than a second';
	}

	if (seconds < 60) {
		return `${seconds} second${seconds === 1 ? '' : 's'}`;
	}

	let minutes = Math.floor(seconds / 60);
	let remainder = seconds % 60;

	if (!remainder) {
		return `${minutes} minute${minutes === 1 ? '' : 's'}`;
	}

	return `${minutes}m ${remainder}s`;
}

export function findOperation(definition: BulkFieldDefinition, operation: BulkOperationId) {
	return definition.operations.find((candidate) => candidate.id === operation);
}

export function buildChangePlan(
	records: any[],
	definition: BulkFieldDefinition,
	operation: BulkOperationId,
	input: string
): BulkChangePlan {
	let operationDef = findOperation(definition, operation);
	let rows: BulkChangeRow[] = [];

	for (let record of records ?? []) {
		if (!record?.id) {
			continue;
		}

		let currentValue = record[definition.field];
		let computed: BulkComputeResult;

		try {
			computed = definition.compute(record, operation, input);
		} catch (error: any) {
			computed = { status: 'blocked', reason: error?.message ?? 'Could not compute a new value' };
		}

		rows.push({
			id: record.id,
			record,
			status: computed.status,
			reason: computed.reason,
			currentValue,
			newValue: computed.newValue,
			remove: computed.remove,
			currentLabel: definition.formatValue(currentValue),
			newLabel:
				computed.status === 'change'
					? computed.remove
						? 'Deleted permanently'
						: definition.formatValue(computed.newValue)
					: definition.formatValue(currentValue),
		});
	}

	return {
		field: definition.field,
		fieldLabel: definition.label,
		operation,
		operationLabel: operationDef?.label ?? operation,
		input,
		destructive: !!operationDef?.destructive,
		rows,
		changeCount: rows.filter((row) => row.status === 'change').length,
		unchangedCount: rows.filter((row) => row.status === 'unchanged').length,
		blockedCount: rows.filter((row) => row.status === 'blocked').length,
	};
}

export function selectableRows(plan: BulkChangePlan) {
	return plan.rows.filter((row) => row.status === 'change');
}

/** The fields a row writes, keyed by field name, whether it changes one or many. */
function rowValues(plan: BulkChangePlan, row: BulkChangeRow, direction: 'new' | 'current') {
	if (row.changes?.length) {
		let values: { [field: string]: any } = {};

		row.changes.forEach((change) => {
			values[change.field] = (direction === 'new' ? change.newValue : change.currentValue) ?? null;
		});

		return values;
	}

	return { [plan.field]: (direction === 'new' ? row.newValue : row.currentValue) ?? null };
}

export function planToWrites(plan: BulkChangePlan, excludedIds: { [id: string]: boolean } = {}): BatchRecordWrite[] {
	return selectableRows(plan)
		.filter((row) => !excludedIds[row.id])
		.map((row) => (row.remove ? { id: row.id, remove: true } : { id: row.id, values: rowValues(plan, row, 'new') }));
}

/**
 * Builds the writes that put the previous values back. Permanent deletions are not
 * recoverable, so they are never part of an undo.
 */
export function planToUndoWrites(plan: BulkChangePlan, appliedIds: string[]): BatchRecordWrite[] {
	let applied = new Set(appliedIds);

	return selectableRows(plan)
		.filter((row) => applied.has(row.id) && !row.remove)
		.map((row) => ({ id: row.id, values: rowValues(plan, row, 'current') }));
}

export function isUndoable(plan: BulkChangePlan) {
	return !plan.destructive && selectableRows(plan).some((row) => !row.remove);
}

/**
 * The plan as an undo would apply it: only the records that actually changed, with the
 * before and after swapped, so the table shows the reversal rather than the original edit.
 */
export function invertPlan(plan: BulkChangePlan, appliedIds: string[]): BulkChangePlan {
	let applied = new Set(appliedIds);

	let rows: BulkChangeRow[] = selectableRows(plan)
		.filter((row) => applied.has(row.id) && !row.remove)
		.map((row) => ({
			...row,
			currentValue: row.newValue,
			newValue: row.currentValue,
			currentLabel: row.newLabel,
			newLabel: row.currentLabel,
			changes: row.changes?.map((change) => ({
				...change,
				currentValue: change.newValue,
				newValue: change.currentValue,
				currentLabel: change.newLabel,
				newLabel: change.currentLabel,
			})),
		}));

	return {
		...plan,
		operationLabel: 'Undo',
		rows,
		changeCount: rows.length,
		unchangedCount: 0,
		blockedCount: 0,
	};
}

/**
 * A plain sentence describing the change, for people who do not think in field names.
 *
 * "Date — Shift by days: 2" reads like a database instruction; "Move Date 2 days later"
 * reads like what is about to happen.
 */
export function describeChange(plan: BulkChangePlan): string {
	let field = plan.fieldLabel;
	let input = (plan.input ?? '').trim();

	switch (plan.operation) {
		case 'set': {
			// The formatted value from the table, so the sentence and the rows agree.
			let sample = plan.rows.find((row) => row.status === 'change')?.newLabel ?? input;
			return `Set ${field} to ${sample}`;
		}
		case 'increase':
			return `Increase ${field} by ${input}`;
		case 'decrease':
			return `Decrease ${field} by ${input}`;
		case 'shiftDays':
		case 'shiftHours': {
			let amount = parseInt(input, 10);
			let unit = plan.operation === 'shiftDays' ? 'day' : 'hour';
			let size = Math.abs(isNaN(amount) ? 0 : amount);
			return `Move ${field} ${size} ${unit}${size === 1 ? '' : 's'} ${amount < 0 ? 'earlier' : 'later'}`;
		}
		case 'approve':
			return 'Approve these records';
		case 'reject':
			return 'Reject these records';
		case 'softDelete':
			return 'Move these records to deleted';
		case 'restore':
			return 'Restore these records';
		case 'permanentDelete':
			return 'Permanently delete these records';
		default:
			return `${field} — ${plan.operationLabel}`;
	}
}
